import { Link, useParams } from "react-router-dom";
import { getNewsById, newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const news = id ? getNewsById(id) : undefined;

  usePageMeta(
    news ? `${news.title} - 善良的秘书` : "资讯不存在 - 善良的秘书",
    news ? news.summary : "该资讯不存在或已下线，返回资讯专题页浏览更多内容。"
  );

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 py-16">
        <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news" }, { label: "资讯详情" }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-file-unknow-line"
            title="资讯不存在或已下线"
            description="你访问的资讯可能已经被移除，返回资讯专题页看看其他内容。"
            action={
              <Link
                to="/news"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-newspaper-line"></i>
                返回资讯专题
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const related = newsList.filter((n) => n.id !== news.id && n.category === news.category).slice(0, 3);
  const more = related.length > 0 ? related : newsList.filter((n) => n.id !== news.id).slice(0, 3);

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb
        crumbs={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: news.category },
        ]}
      />

      <article className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-md bg-accent-100 px-2 py-0.5 text-accent-900">{news.category}</span>
            <span className="font-label text-foreground-500">发布于 {news.date}</span>
          </div>
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-black leading-snug text-foreground-950">
            {news.title}
          </h1>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">{news.summary}</p>

          <div className="mt-6 w-full overflow-hidden rounded-lg border border-background-200">
            <img
              src={news.thumbnail}
              alt={`${news.title} 配图`}
              title={`${news.title} 资讯配图`}
              className="w-full object-cover object-top"
              style={{ aspectRatio: "16 / 9" }}
            />
          </div>

          <div className="mt-8 space-y-4 text-sm md:text-base leading-relaxed text-foreground-700">
            {news.content.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
            <span className="text-sm text-foreground-500">分享这篇资讯：</span>
            <button
              type="button"
              className="flex h-9 items-center gap-2 rounded-md border border-background-200 px-4 text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              <i className="ri-weibo-fill text-primary-600"></i>
              微博
            </button>
            <button
              type="button"
              className="flex h-9 items-center gap-2 rounded-md border border-background-200 px-4 text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              <i className="ri-link"></i>
              复制链接
            </button>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-links-line text-accent-600"></i>
              相关阅读
            </h2>
            <div className="mt-4 space-y-4">
              {more.map((n) => (
                <Link key={n.id} to={`/news/${n.id}`} className="group flex gap-3">
                  <span className="h-16 w-24 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={n.thumbnail}
                      alt={`${n.title} 配图`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {n.title}
                    </span>
                    <span className="mt-1 block font-label text-xs text-foreground-500">{n.date}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-primary-900 p-5 text-primary-100">
            <h2 className="font-heading text-base font-bold text-background-50">追剧不停歇</h2>
            <p className="mt-2 text-sm leading-relaxed text-primary-200">
              看完资讯，去剧集详情页了解剧情与主演，把想看的加进清单。
            </p>
            <Link
              to="/category"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-primary-950 whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors"
            >
              <i className="ri-movie-2-line"></i>
              浏览热门剧集
            </Link>
          </div>
        </aside>
      </article>
    </div>
  );
}